import React from "react";
import { KpiCard, KpiGrid, PageHeader, Panel, Table, StatusChip, useAdminQuery, RefreshButton, Unavailable, fmtDate, INDIGO, AQUA, MUTE } from "../_shared";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function AdminOverview() {
  const m   = useAdminQuery("/admin/metrics");
  const ts  = useAdminQuery("/admin/metrics/timeseries", { days: 30 });
  const jobs = useAdminQuery("/admin/reports/jobs", { page: 1, page_size: 6 });
  const health = useAdminQuery("/admin/health");

  if (m.loading || ts.loading) return <Loading />;
  if (m.error) return <Unavailable error={m.error} />;
  const d = m.data || {};
  const active_jobs = (health.data?.pending_jobs) ?? "—";
  const todaySignups = (ts.data?.signups || []).slice(-1)[0]?.value ?? 0;

  return (
    <div data-testid="admin-overview">
      <PageHeader title="Overview"
        subtitle={`Last updated ${fmtDate(d.generated_at)} · UTC`}
        right={<RefreshButton loading={m.loading || ts.loading} onClick={() => { m.refresh(); ts.refresh(); }} />} />
      <KpiGrid>
        <KpiCard label="Total users"       value={d.signups?.total}    testId="kpi-total-users"/>
        <KpiCard label="Active 24h (DAU)"  value={d.activity?.dau}     testId="kpi-dau"/>
        <KpiCard label="New today"         value={todaySignups}        testId="kpi-today"/>
        <KpiCard label="Life Reports"      value={d.reports?.total}    testId="kpi-reports"/>
        <KpiCard label="Credits spent"     value={d.credits?.spent}    testId="kpi-spent"/>
        <KpiCard label="Active jobs"       value={active_jobs}         testId="kpi-active-jobs"/>
      </KpiGrid>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <Panel title="Signups · 30 days" testId="panel-signups-trend">
          <Trend data={ts.data?.signups} color={INDIGO} />
        </Panel>
        <Panel title="Credits spent · 30 days" testId="panel-credits-trend">
          <Trend data={ts.data?.credits_spent} color={AQUA} />
        </Panel>
      </div>

      <Panel title="Recent Life Reports" testId="panel-recent-reports">
        {jobs.loading ? <Loading inline /> : jobs.error ? <Unavailable error={jobs.error} /> : (
          <Table cols={[
            { label: "Job",       render: (r) => <code style={{ fontSize: 11 }}>{(r.job_id || "").slice(0,10)}</code> },
            { label: "User",      render: (r) => <code style={{ fontSize: 11 }}>{(r.user_id || "").slice(0,14)}</code> },
            { label: "Status",    render: (r) => <StatusChip status={r.status} /> },
            { label: "Created",   render: (r) => fmtDate(r.created_at) },
          ]} rows={(jobs.data?.rows || []).slice(0, 6)} />
        )}
      </Panel>

      <Panel title="Latest operational alerts" testId="panel-alerts">
        {(health.data?.services || []).filter((s) => s.status !== "healthy").length === 0 ? (
          <div style={{ color: MUTE, fontSize: 13 }}>All systems healthy.</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0,
                        display: "flex", flexDirection: "column", gap: 6 }}>
            {(health.data?.services || []).filter((s) => s.status !== "healthy").map((s, i) => (
              <li key={i} style={{ display: "flex", gap: 10, fontSize: 13 }}>
                <StatusChip status={s.status} /> <strong>{s.service}</strong>
                <span style={{ color: MUTE }}>{s.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function Trend({ data, color }) {
  const rows = (data || []).map((d) => ({ day: d.date.slice(5), v: d.value }));
  if (!rows.length) return <div style={{ color: MUTE, fontSize: 12 }}>No data.</div>;
  return (
    <ResponsiveContainer width="100%" height={140}>
      <AreaChart data={rows}>
        <defs>
          <linearGradient id={`g-${color}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0.3" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <XAxis dataKey="day" tick={{ fontSize: 9, fill: MUTE }} hide />
        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E8E6EF" }} />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#g-${color})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
function Loading({ inline }) {
  return <div style={{ padding: inline ? 10 : 40, color: MUTE, fontSize: 13, textAlign: "center" }}>Loading…</div>;
}
